import { CommandProps, Content } from '@tiptap/core';
import { Node as ProseMirrorNode } from 'prosemirror-model';
import { ForwardedRef, useImperativeHandle } from 'react';

import { UseActionInputState } from '../../../hooks/use-action-input-state';
import { ActionData } from '../../../lib/entities';
import { RichMediaState } from '../../../lib/state';
import { Validation, ValidationError } from '../../../lib/validation';
import { actionData } from '../../../utils/data';

interface UseActionHandlersOptions {
	editor: any;
	inputState: RichMediaState;
	reset: VoidFunction;
	setErrors: (errors: ValidationError[]) => void;
	state: UseActionInputState;
}

export interface ActionInputHandle {
	data: () => ActionData;
	error: (...errors: (string | ValidationError)[]) => void;
	focus: VoidFunction;
	insert: (content: Content) => void;
	insertBeforeTagNodes: (content: string) => void;
	insertWithHighlight: (content: string) => void;
	reset: VoidFunction;
}

/**
 * Determines the leading separator to use when inserting `content` after `precedingText`.
 * - No separator if preceding text already ends with a space.
 * - ". " when the suggestion starts a new sentence (Unicode uppercase letter) and preceding text has no sentence-ending punctuation.
 * - " " otherwise (including when the preceding node is an atom like a mention).
 */
function sentenceSeparator(precedingText: string, content: string, precedingIsAtom: boolean): string {
	if (precedingIsAtom) return ' ';
	if (!precedingText || precedingText.endsWith(' ')) return '';
	if (/[.!?]$/.test(precedingText)) return ' ';
	if (/^\p{Lu}/u.test(content)) return '. ';
	return ' ';
}

function isWhitespaceTextNode(node: ProseMirrorNode): boolean {
	return node.type.name === 'text' && node.textContent.trim() === '';
}

export function insertionPointBeforeTrailingHashtags(doc: ProseMirrorNode): number | null {
	const leaves: { node: ProseMirrorNode; pos: number }[] = [];

	doc.descendants((node: ProseMirrorNode, pos: number) => {
		if (node.isLeaf) leaves.push({ node, pos });
	});

	if (leaves.length === 0) return null;

	let trailingHashtagGroupStart: number | null = null;
	let trailingWhitespaceEnd: number | null = null;

	for (let i = leaves.length - 1; i >= 0; i -= 1) {
		const { node, pos } = leaves[i];

		if (isWhitespaceTextNode(node)) {
			if (trailingHashtagGroupStart !== null) {
				trailingHashtagGroupStart = pos;
				continue;
			}

			trailingWhitespaceEnd ??= pos + node.nodeSize;
			continue;
		}

		if (node.type.name === 'hashtag') {
			trailingHashtagGroupStart = pos;
			trailingWhitespaceEnd = null;
			continue;
		}

		return trailingHashtagGroupStart ?? trailingWhitespaceEnd ?? pos + node.nodeSize;
	}

	return trailingHashtagGroupStart ?? trailingWhitespaceEnd;
}

function hasWhitespaceAfter(doc: ProseMirrorNode, pos: number): boolean {
	if (pos >= doc.content.size) return false;

	const nextChar = doc.textBetween(pos, Math.min(pos + 1, doc.content.size));
	return /\s/.test(nextChar);
}

export const useActionHandle = (ref: ForwardedRef<any>, { editor, inputState, reset, setErrors, state }: UseActionHandlersOptions) => {
	useImperativeHandle(ref, () => ({
		data: () => {
			return actionData({
				...state,
				state: inputState,
			});
		},
		error: (...errors: (string | ValidationError)[]) => {
			const validations = errors.map((error) => {
				if (error instanceof ValidationError) {
					return error;
				}

				return new ValidationError(Validation.ServerError, error);
			});

			setErrors(validations);
		},
		focus: () => {
			editor?.commands.focus();
		},
		insert(content: Content) {
			editor?.commands.insertContent(content);
		},
		insertBeforeTagNodes(content: string) {
			if (!editor) return;

			const editorState = editor.view.state;
			const insertPos = insertionPointBeforeTrailingHashtags(editorState.doc);

			const trailingSpace = content.endsWith(' ') || (insertPos !== null && hasWhitespaceAfter(editorState.doc, insertPos)) ? '' : ' ';

			if (insertPos !== null) {
				const $pos = editorState.doc.resolve(insertPos);
				const nodeBefore = $pos.nodeBefore;
				const precedingText = nodeBefore?.isAtom ? '' : (nodeBefore?.textContent ?? '');
				const leading = sentenceSeparator(precedingText, content, nodeBefore != null && nodeBefore.isAtom);
				editor.chain().focus().insertContentAt(insertPos, `${leading}${content}${trailingSpace}`).run();
			} else {
				const { from } = editorState.selection;
				const precedingChar = editorState.doc.textBetween(Math.max(0, from - 1), from);
				const leading = sentenceSeparator(precedingChar, content, false);
				editor.chain().focus().insertContent(`${leading}${content}${trailingSpace}`).run();
			}
		},
		insertWithHighlight(content: string) {
			if (!editor) return;

			const editorState = editor.view.state;
			const insertPos = insertionPointBeforeTrailingHashtags(editorState.doc);

			const trailingSpace = content.endsWith(' ') || (insertPos !== null && hasWhitespaceAfter(editorState.doc, insertPos)) ? '' : ' ';

			const doInsertAndMark = (insertAt: number, precedingText: string, precedingIsAtom: boolean) => {
				const leading = sentenceSeparator(precedingText, content, precedingIsAtom);
				const fullContent = `${leading}${content}${trailingSpace}`;

				editor.chain().focus().insertContentAt(insertAt, fullContent).run();

				const markFrom = insertAt + leading.length;
				const markTo = markFrom + content.length;

				editor.commands.command(({ tr, state }: CommandProps) => {
					const markType = state.schema.marks['suggestion-highlight'];
					if (!markType || markFrom >= markTo) return false;
					tr.addMark(markFrom, markTo, markType.create());
					tr.setMeta('suggestion-highlight-insertion', true);
					return true;
				});
			};

			if (insertPos !== null) {
				const $pos = editorState.doc.resolve(insertPos);
				const nodeBefore = $pos.nodeBefore;
				const precedingText = nodeBefore?.isAtom ? '' : (nodeBefore?.textContent ?? '');
				doInsertAndMark(insertPos, precedingText, nodeBefore != null && nodeBefore.isAtom);
			} else {
				const { from } = editorState.selection;
				const precedingChar = editorState.doc.textBetween(Math.max(0, from - 1), from);
				doInsertAndMark(from, precedingChar, false);
			}
		},
		reset: () => {
			editor?.commands.clearContent();
			reset();
		},
	}));
};
